import React, { useState } from 'react';
import { Building2, Copy, Trash2, RefreshCw } from 'lucide-react';

const FakeCompanyGeneratorTool = () => {
  const [count, setCount] = useState(5);
  const [industry, setIndustry] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [copied, setCopied] = useState('');

  const prefixes = [
    'Alpha', 'Beta', 'Cyber', 'Digital', 'Echo', 'First', 'Global', 'Horizon', 'Infinite', 'Jupiter',
    'Kinetic', 'Lunar', 'Momentum', 'Nova', 'Omega', 'Prime', 'Quantum', 'Rapid', 'Stellar', 'Titan',
    'Ultra', 'Vortex', 'Wave', 'Xenon', 'Yield', 'Zenith', 'Apex', 'Bright', 'Core', 'Dynamic'
  ];

  const suffixes = {
    tech: ['Tech', 'Systems', 'Software', 'Digital', 'Labs', 'AI', 'Cloud', 'Data', 'Networks', 'Solutions'],
    finance: ['Capital', 'Investments', 'Financial', 'Holdings', 'Ventures', 'Partners', 'Advisors', 'Trust', 'Wealth', 'Asset'],
    healthcare: ['Health', 'Medical', 'Therapeutics', 'Pharma', 'Bio', 'Life Sciences', 'Care', 'Wellness', 'Diagnostics', 'MedTech'],
    retail: ['Retail', 'Commerce', 'Brands', 'Markets', 'Goods', 'Stores', 'Trading', 'Merchants', 'Supply', 'Outlet'],
    manufacturing: ['Industries', 'Manufacturing', 'Products', 'Materials', 'Engineering', 'Works', 'Fabrication', 'Assembly', 'Parts', 'Components']
  };

  const industries = {
    all: 'All Industries',
    tech: 'Technology',
    finance: 'Finance',
    healthcare: 'Healthcare',
    retail: 'Retail',
    manufacturing: 'Manufacturing'
  };

  const domains = ['.com', '.io', '.co', '.net', '.tech', '.ai', '.biz'];

  const generateCompany = () => {
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    
    let industryKey = industry;
    if (industry === 'all') {
      const keys = Object.keys(suffixes);
      industryKey = keys[Math.floor(Math.random() * keys.length)];
    }
    
    const suffixList = suffixes[industryKey];
    const suffix = suffixList[Math.floor(Math.random() * suffixList.length)];
    const domain = domains[Math.floor(Math.random() * domains.length)];
    
    const name = `${prefix} ${suffix}`;
    const website = `www.${prefix.toLowerCase()}${suffix.toLowerCase().replace(/\s+/g, '')}${domain}`;
    const email = `contact@${prefix.toLowerCase()}${suffix.toLowerCase().replace(/\s+/g, '')}${domain}`;
    const founded = 2000 + Math.floor(Math.random() * 24);
    const employees = [10, 50, 100, 250, 500, 1000, 5000, 10000][Math.floor(Math.random() * 8)];
    
    return {
      name,
      industry: industries[industryKey],
      website,
      email,
      founded,
      employees: `${employees}+`,
      slogan: generateSlogan(industryKey)
    };
  };

  const generateSlogan = (industryKey) => {
    const slogans = {
      tech: [
        'Innovating Tomorrow, Today',
        'Where Technology Meets Innovation',
        'Building the Digital Future',
        'Smarter Solutions for a Connected World'
      ],
      finance: [
        'Your Financial Success, Our Priority',
        'Investing in Your Future',
        'Wealth Management Redefined',
        'Smart Money, Smart Decisions'
      ],
      healthcare: [
        'Caring for Your Health',
        'Advancing Medical Excellence',
        'Better Health, Better Life',
        'Pioneering Healthcare Solutions'
      ],
      retail: [
        'Quality Products, Great Prices',
        'Shopping Made Simple',
        'Your Satisfaction Guaranteed',
        'Where Quality Meets Value'
      ],
      manufacturing: [
        'Building Excellence',
        'Precision Engineering Solutions',
        'Quality You Can Trust',
        'Manufacturing the Future'
      ]
    };
    const list = slogans[industryKey];
    return list[Math.floor(Math.random() * list.length)];
  };

  const generateCompanies = () => {
    const newCompanies = [];
    for (let i = 0; i < count; i++) {
      newCompanies.push(generateCompany());
    }
    setCompanies(newCompanies);
  };

  const copyCompany = (company, index) => {
    const text = `${company.name}
Industry: ${company.industry}
Website: ${company.website}
Email: ${company.email}
Founded: ${company.founded}
Employees: ${company.employees}
Slogan: "${company.slogan}"`;
    navigator.clipboard.writeText(text);
    setCopied(index);
    setTimeout(() => setCopied(''), 1500);
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Industry</label>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          >
            {Object.entries(industries).map(([key, name]) => (
              <option key={key} value={key}>{name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Count</label>
          <input
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
            min="1"
            max="20"
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={generateCompanies}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        >
          <Building2 size={18} />
          Generate Companies
        </button>
        <button
          onClick={generateCompanies}
          disabled={companies.length === 0}
          className="flex items-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {companies.length > 0 && (
        <div className="grid gap-4">
          {companies.map((company, i) => (
            <div key={i} className="p-4 bg-gray-800 rounded-lg">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-white">{company.name}</h3>
                  <p className="text-sm text-blue-400">{company.industry}</p>
                </div>
                <button
                  onClick={() => copyCompany(company, i)}
                  className={`p-2 rounded transition-colors ${
                    copied === i ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <Copy size={16} />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-gray-500">Website</div>
                  <div className="text-gray-300">{company.website}</div>
                </div>
                <div>
                  <div className="text-gray-500">Email</div>
                  <div className="text-gray-300">{company.email}</div>
                </div>
                <div>
                  <div className="text-gray-500">Founded</div>
                  <div className="text-gray-300">{company.founded}</div>
                </div>
                <div>
                  <div className="text-gray-500">Employees</div>
                  <div className="text-gray-300">{company.employees}</div>
                </div>
              </div>
              <p className="mt-3 text-sm text-gray-400 italic">"{company.slogan}"</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => setCompanies([])}
        disabled={companies.length === 0}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default FakeCompanyGeneratorTool;
